import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Spinner from "../../components/common/Spinner";
import Container from "../../components/Layout/Container";
import styled from "styled-components";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import TableFilterBox from "../../components/common/TableFilterBox";
import { Input, Form, Button, Checkbox, Select } from "antd";
import {
  createShopCategory,
  getBrandsOptions,
  updateShopCategory,
} from "../../redux/Actions/shopCategoriesAction";
import TextInputModal from "../../components/common/TextInputModal";
import { handleKeyDown } from "../../helpers/FunctionsGlobal";
import { CheckBoxInput, SelectForm } from "../../components/common/Buttons";
import { antdAsyncMessageSuccess } from "../../helpers/AsyncMessage";
import axios from "axios";
import { IconPlus, IconTrash } from "../../components/Layout/Icons";
import ModalSimple from "../../components/Layout/Modals/ModalSimple";
import { ButtonEditRow } from "../../components/common/Buttons";

const SHOP_IDENTIFIER_MAP = {
  "BIGBOX LT": "BB",
  "BIGBOX EE": "BBE",
  "BIGBOX LV": "BBL",
  "BIGBOX FI": "BBF",
  "TOYBOX LT": "TB",
  "TECHNORAMA LT": "TCH",
  "VERSLO VARTAI": "VV",
};

const LANG_IDENTIFIER_MAP = {
  "BIGBOX LT": "LT",
  "BIGBOX EE": "EE",
  "BIGBOX LV": "LV",
  "BIGBOX FI": "FI",
  "TOYBOX LT": "LT",
  "TECHNORAMA LT": "LT",
  "VERSLO VARTAI": "LT",
};

const setShopIndentifier = (shopTitle) => SHOP_IDENTIFIER_MAP[shopTitle] || "";
const setLangIndentifier = (shopTitle) => LANG_IDENTIFIER_MAP[shopTitle] || "";

function CategoriesScreen({ match }) {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categoryInputs, setCategoryInputs] = useState({});
  const [selected, setSelected] = useState({
    "title": "Root",
    "children": [
      {
        "title": "BIGBOX LT",
        "children": []
      }
    ]
  });
  const [filterString, setFilterString] = useState("");
  const [currentData, setCurrentData] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const url = filterString
        ? `/api/v1/shop_categories?${filterString}`
        : `/api/v1/shop_categories`;
      const { data } = await axios.get(url);
      setCurrentData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterString]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCategoryInputChange = useCallback((categoryId, field, value) => {
    setCategoryInputs((prev) => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || {}),
        [field]: value,
      },
    }));
  }, []);

  useEffect(() => {
    // Fetch brands
    getBrandsOptions().then((res) => {
      setBrands(res.map((d) => ({ value: d.id, label: d.title })));
    }).catch(err => {
      console.error("Error fetching brands:", err);
    });
  }, []);

  return (
    <Container location={{ locationName: "Categories", locationUrl: match.url }}>
      <div className="col-12">
        <Filters setCurrentData={setCurrentData} brands={brands} setFilterString={setFilterString} />
      </div>
      <div className="col-12 my-3 d-flex justify-content-between">
        {currentData?.map((d, idx) => {
          const rootCategory = d?.children?.[0];
          return (
            <div key={`${rootCategory?.title}-${idx}`}>
              <ButtonStyled
                active={selected && rootCategory?.title === selected?.children[0]?.title}
                onClick={() => setSelected(d)}
              >
                {rootCategory?.title}
              </ButtonStyled>
            </div>
          );
        })}
      </div>

      <div className="mb-5">
        {loading && <Spinner />}
        {currentData?.map((d, index) => {
          const rootCategory = d?.children?.[0];
          return (
            <div key={`${rootCategory?.title}+${index}`}>
              {selected && rootCategory?.title === selected?.children[0]?.title && (
                <div className="col-12">
                  {rootCategory?.children[0]?.children?.map((child, idx) => (
                    <PreviewCategory
                      key={`${child.id}+${idx}+${index}+${child.title}`}
                      child={child}
                      brands={brands}
                      selected={selected}
                      setSelected={setSelected}
                      setCurrentData={setCurrentData}
                      categoryInputs={categoryInputs}
                      onInputChange={handleCategoryInputChange}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Container>
  );
}

const Filters = ({ brands, setFilterString }) => {
  const initialState = {
    name_cont: "",
    shop_categories_group_cont: "",
    shop_categories_brand_cont: undefined,
    shop_categories_warranty_eq: "",
    shop_categories_without_services_eq: "",
  };
  const [filters, setFilters] = useState(initialState);

  useEffect(() => {
    onFilter();
  }, [filters.shop_categories_brand_cont, filters.shop_categories_warranty_eq, filters.shop_categories_without_services_eq]);
  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onFilter = () => {
    let filtStr = "";
    Object.entries(filters).forEach((d) => {
      if (d[1]) {
        filtStr += `&q[${d[0]}]=${d[1].toLowerCase()}`;
      }
    });
    setFilterString(filtStr);
  };
  return (
    <TableFilterBox
      extraClass="shadow py-2 rounded"
      clearFilter={() => {
        setFilters(initialState);
        setFilterString("");
      }}
    >
      <div className="col-12">
        <div className="row">
          <div className="col-2">
            <TextInputModal
              text="Category"
              name="name_cont"
              onKeyDown={(e) => handleKeyDown(e, onFilter)}
              value={filters.name_cont}
              onChange={onChange}
            />
          </div>
          <div className="col-2">
            <TextInputModal
              text="Group"
              name="shop_categories_group_cont"
              onKeyDown={(e) => handleKeyDown(e, onFilter)}
              value={filters.shop_categories_group_cont}
              onChange={onChange}
            />
          </div>
          <div className="col-2">
            <SelectForm
              options={[{ value: "", label: "All" }, ...brands]}
              text="Brand"
              name="shop_categories_brand_cont"
              placeholder="Brand"
              onlyPlaceholder="Choose"
              value={filters.shop_categories_brand_cont || undefined}
              onChange={(value) =>
                onChange({ target: { value, name: "shop_categories_brand_cont" } })
              }
            />
          </div>

          <div className="col-2">
            <SelectForm
              options={[
                { value: "", label: "All" },
                { value: "1", label: "Yes" },
                { value: "0", label: "No" },
              ]}
              text="Warranty"
              name="shop_categories_warranty_eq"
              placeholder="Warranty"
              onlyPlaceholder="Choose"
              value={filters.shop_categories_warranty_eq}
              onChange={(value) =>
                onChange({ target: { value, name: "shop_categories_warranty_eq" } })
              }
            />
          </div>
          <div className="col-2">
            <SelectForm
              options={[
                { value: "", label: "All" },
                { value: "1", label: "Yes" },
                { value: "0", label: "No" },
              ]}
              text="Without services"
              name="shop_categories_without_services_eq"
              placeholder="Without services"
              onlyPlaceholder="Choose"
              value={filters.shop_categories_without_services_eq}
              onChange={(value) =>
                onChange({ target: { value, name: "shop_categories_without_services_eq" } })
              }
            />
          </div>
        </div>
      </div>
    </TableFilterBox>
  );
};

const PreviewCategory = ({
  child,
  brands,
  selected,
  setSelected,
  setCurrentData,
  categoryInputs,
  onInputChange,
}) => {
  const [preview, setPreview] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const children_length = useMemo(() => child?.children?.length || 0, [child]);
  const title = child.title;

  // useCallback to avoid redefining this on every render
  const fetchShopCategoriesWithParams = useCallback(
    async (categoryId, lang, shop) => {
      setLoading(true);
      setError(null);
      try {
        setFetchedData(null);
        const response = await axios.get(
          `/api/v1/shop_categories/${categoryId}?lang=${lang}&shop=${shop}`
        );
        setFetchedData(response.data);
      } catch (err) {
        setError(err.response?.data || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handlePreviewClick = useCallback(() => {
    setPreview((prev) => !prev);
    if (!preview) {
      fetchShopCategoriesWithParams(
        child.category_id,
        setLangIndentifier(selected.children[0].title),
        setShopIndentifier(selected.children[0].title)
      );
    }
  }, [
    preview,
    child.category_id,
    selected,
    fetchShopCategoriesWithParams,
  ]);

  const refetch = useCallback(() => {
    fetchShopCategoriesWithParams(
      child.category_id,
      setLangIndentifier(selected.children[0].title),
      setShopIndentifier(selected.children[0].title)
    );
  }, [child.category_id, selected, fetchShopCategoriesWithParams]);

  return (
    <div key={selected} className="bg-white rounded">
      <div className="mb-2 bg-white p-0 shadow rounded">
        <div className="col-12" style={{ borderBottom: preview ? "1px solid #BFBFBF" : "none" }}>
          <div className="row">
            <div className="col-9 py-3 text-black font-semi-bold">
              {title} ({children_length})
            </div>
            <div className="col-3 py-3 d-flex justify-content-end align-items-center cursor-pointer" onClick={handlePreviewClick}>
              {preview ? (
                <AiOutlineUp className="text-black" />
              ) : (
                <AiOutlineDown className="text-black" />
              )}
            </div>
          </div>
        </div>
        {preview && loading && <Spinner />}
        {preview && error && <div className="text-danger">Error: {error}</div>}
        {preview &&
          !!children_length &&
          fetchedData?.[0]?.children?.map((c, idx) => (
            <PreviewCategoryChildren
              key={`${c.id}-${c.category_id}-${idx}`}
              child={c}
              brands={brands}
              selected={selected}
              setSelected={setSelected}
              setCurrentData={setCurrentData}
              categoryInputs={categoryInputs}
              onInputChange={onInputChange}
              main={c.main}
              refetch={refetch}
            />
          ))}
      </div>
    </div>
  );
};

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

const PreviewCategoryChildren = ({
  warranty,
  brands,
  padding = 0,
  main,
  selected,
  setCurrentData,
  child,
  categoryInputs,
  onInputChange,
  refetch,
}) => {
  const [preview, setPreview] = useState(false);
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const updateRef = useRef(update);
  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  const values = useMemo(() => {
    const data = fetchedData?.[0] || child;
    const brandCodes = data.brand_codes && data.brand_codes.length > 0
      ? data.brand_codes.map((bc, index) => ({
        id: Date.now() + index,
        brand_id: bc.brand_id,
        code: bc.brand_code,
        profit: bc.brand_profit || ''
      }))
      : [{ id: Date.now(), brand_id: data.brand || null, code: data.brand_code || '' }];
    return {
      group: data.group,
      brand: data.brand,
      brandCodes: brandCodes,
      warranty: data.warranty ?? warranty,
      withoutServices: data.without_services,
      customsCode: data.customs_code,
      reverseCharging: data.reverse_charging,
      managerCode: data.manager_code,
      agataCode: data.agata_code,
      agataPrice: data.agata_price,
      profit: data.profit
    };
  }, [fetchedData, child, warranty]);

  const initialValues = useMemo(() => {
    const data = fetchedData?.[0] || child;
    const brandCodes = data.brand_codes && data.brand_codes.length > 0
      ? data.brand_codes.map((bc, index) => ({
        id: Date.now() + index,
        brand_id: bc.brand_id,
        code: bc.brand_code,
        profit: bc.brand_profit || ''
      }))
      : [{ id: Date.now(), brand_id: data.brand || null, code: data.brand_code || '' }];
    return {
      group: data.group,
      brand: data.brand,
      brandCodes: brandCodes,
      warranty: data.warranty ?? warranty,
      withoutServices: data.without_services,
      customsCode: data.customs_code,
      reverseCharging: data.reverse_charging,
      managerCode: data.manager_code,
      agataCode: data.agata_code,
      agataPrice: data.agata_price,
      profit: data.profit
    };
  }, [fetchedData, child, warranty]);


  const addBrandCode = () => {
    setLocalValues(prev => {
      const newBrandCodes = [...(prev.brandCodes || []), {
        id: Date.now(),
        brand_id: null,
        code: ''
      }];

      return { ...prev, brandCodes: newBrandCodes };
    });
  };

  const removeBrandCode = (idToRemove) => {
    setLocalValues(prev => {
      const newBrandCodes = (prev.brandCodes || []).filter(item => item.id !== idToRemove);
      const newState = { ...prev, brandCodes: newBrandCodes };

      const removedItem = prev.brandCodes.find(item => item.id === idToRemove);
      if (newBrandCodes.length > 0 && (removedItem.brand_id || removedItem.code)) {
        debouncedUpdate.current(newState);
      }

      return newState;
    });
  };

  const updateBrandId = (id, newBrandId) => {
    setLocalValues(prev => {
      const newBrandCodes = (prev.brandCodes || []).map(item =>
        item.id === id ? { ...item, brand_id: newBrandId } : item
      );

      const newState = { ...prev, brandCodes: newBrandCodes };
      debouncedUpdate.current(newState);
      return newState;
    });
  }

  const updateBrandCode = (id, newCode) => {
    setLocalValues(prev => {
      const newBrandCodes = (prev.brandCodes || []).map(item =>
        item.id === id ? { ...item, code: newCode } : item
      );

      const newState = {
        ...prev,
        brandCodes: newBrandCodes
      };

      debouncedUpdate.current(newState);
      return newState;
    });
  };

  const updateBrandProfit = (id, newProfit) => {
    setLocalValues(prev => {
      const newBrandCodes = (prev.brandCodes || []).map(item =>
        item.id === id ? { ...item, profit: newProfit } : item
      );

      const newState = {
        ...prev,
        brandCodes: newBrandCodes
      };

      debouncedUpdate.current(newState);
      return newState;
    });
  };

  const [localValues, setLocalValues] = useState(initialValues);
  useEffect(() => {
    setLocalValues(initialValues);
  }, [initialValues]);

  const handleLocalInputChange = (field, val) => {
    setLocalValues((prev) => {
      const newState = { ...prev, [field]: val };
      debouncedUpdate.current(newState);
      return newState;
    });
  };

  useEffect(() => {
    if (!preview) {
      setFetchedData(null);
    }
  }, [preview, selected?.children[0]?.title]);

  const fetchShopCategoriesWithParams = useCallback(async (categoryId, lang, shop) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/shop_categories/${categoryId}?lang=${lang}&shop=${shop}`);
      setFetchedData(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback((updateValues) => {
    const payload = {
      brand: updateValues.brand === undefined ? null : updateValues.brand,
      brand_codes: updateValues.brandCodes ? updateValues.brandCodes.map(item => ({
        brand_id: item.brand_id || updateValues.brand,
        brand_code: item.code,
        brand_profit: item.profit
      })) : [],
      group: updateValues.group,
      warranty: updateValues.warranty,
      without_services: updateValues.withoutServices,
      customs_code: updateValues.customsCode,
      reverse_charging: updateValues.reverseCharging,
      manager_code: updateValues.managerCode,
      agata_code: updateValues.agataCode,
      agata_price: updateValues.agataPrice,
      profit: updateValues.profit
    };

    if (child.id) {
      updateShopCategory(child.id, payload)
        .then(refetch)
        .finally(() => {
          antdAsyncMessageSuccess({
            text: "Successfully updated",
            key: "update_shop_category",
          });
        });
    } else {
      createShopCategory(child.category_id, {
        ...payload,
        category_id: child.category_id,
        identifier: setShopIndentifier(selected.children[0].title),
      })
        .then(refetch)
        .finally(() => {
          antdAsyncMessageSuccess({
            text: "Successfully created",
            key: "create_shop_category",
          });
        });
    }
  }, [child.id, child.category_id, selected, refetch]);

  const debouncedUpdate = useRef(
    debounce((vals) => {
      updateRef.current(vals);
    }, 500)
  );

  const handlePreviewClick = useCallback(() => {
    setPreview((prev) => !prev);
    if (!preview) {
      fetchShopCategoriesWithParams(
        child.category_id,
        setLangIndentifier(selected.children[0].title),
        setShopIndentifier(selected.children[0].title)
      );
    }
  }, [preview, child.category_id, selected, fetchShopCategoriesWithParams]);

  const handleInputChange = (field) => (val) => {
    debouncedUpdate.current({
      ...values,
      [field]: val,
    });
  };

  const refetchChild = useCallback(() => {
    fetchShopCategoriesWithParams(
      child.category_id,
      setLangIndentifier(selected.children[0].title),
      setShopIndentifier(selected.children[0].title)
    );
  }, [child.category_id, selected, fetchShopCategoriesWithParams]);

  const children_length = useMemo(() => child.children?.length || 0, [child.children]);

  console.log(localValues.brandCodes, 'localValues.brandCodes');
  return (
    <>
      <div className="col-12 tableColumn rounded">
        <div className="row">
          <div className="col-9 py-3 d-flex align-items-center">
            <div style={{ marginLeft: padding }}>
              <CheckBoxInput
                className="checkbox_blue"
                checked={localValues.reverseCharging}
                onChange={(e) =>
                  handleLocalInputChange("reverseCharging", e.target.checked)
                }
                text={`${child.title} (${children_length})`}
                textColor={main ? "#ff0000" : "#000000"}
              />
            </div>
          </div>
          <div className="col-3 py-3 d-flex justify-content-end align-items-center">
            {!!children_length && (
              <div className="d-flex align-items-center cursor-pointer mr-2" onClick={handlePreviewClick}>
                <span className="mr-1">{children_length}</span>
                {preview ? (
                  <AiOutlineUp className="text-black" />
                ) : (
                  <AiOutlineDown className="text-black" />
                )}
              </div>
            )}
            <ButtonEditRow onClick={() => setIsEditOpen(true)} />
          </div>
        </div>
      </div>

      {isEditOpen && (
        <ModalEditCategory
          visible={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          initialData={values}
          brands={brands}
          child={child}
          selected={selected}
          refetch={refetchChild}
        />
      )}

      {preview && loading && <Spinner />}
      {preview &&
        !!children_length &&
        fetchedData?.[0]?.children?.map((c) => (
          <PreviewCategoryChildren
            key={`${c.id}-${padding + 20}-${c.category_id}`}
            child={c}
            brands={brands}
            padding={padding + 20}
            selected={selected}
            main={c.main}
            setCurrentData={setCurrentData}
            categoryInputs={categoryInputs}
            onInputChange={onInputChange}
            refetch={refetchChild}
            warranty={localValues.warranty}
          />
        ))}
    </>
  );
};

export default CategoriesScreen;


const ButtonStyled = styled.div`
  padding: 20px 50px;
  border-top: 4px solid ${(props) => (props.active ? "#0076A7" : "#03b0f7")};
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.075);
  background: ${(props) => (props.active ? "#03B0F7" : "#ffffff")};
  color: ${(props) => (props.active ? "#FFF" : "#212121")};
  font-family: Barlow;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 22px;
  cursor: pointer;
`;

const ModalEditCategory = ({ visible, onClose, initialData, brands, child, selected, refetch }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        group: initialData.group,
        brand: initialData.brand,
        brandCodes: (initialData.brandCodes || []).map((bc) => ({
          brand_id: bc.brand_id || null,
          code: bc.code || "",
          profit: bc.profit || "",
        })),
        warranty: initialData.warranty,
        withoutServices: initialData.withoutServices,
        customsCode: initialData.customsCode,
        reverseCharging: initialData.reverseCharging,
        managerCode: initialData.managerCode,
        agataCode: initialData.agataCode,
        agataPrice: initialData.agataPrice,
        profit: initialData.profit,
      });
    }
  }, [visible, initialData, form]);

  const handleFinish = (values) => {
    const payload = {
      brand: values.brand === undefined ? null : values.brand,
      brand_codes: (values.brandCodes || []).map((item) => ({
        brand_id: item.brand_id || values.brand || null,
        brand_code: item.code,
        brand_profit: item.profit,
      })),
      group: values.group,
      warranty: values.warranty,
      without_services: values.withoutServices,
      customs_code: values.customsCode,
      reverse_charging: values.reverseCharging,
      manager_code: values.managerCode,
      agata_code: values.agataCode,
      agata_price: values.agataPrice,
      profit: values.profit,
    };

    const request = child.id
      ? updateShopCategory(child.id, payload)
      : createShopCategory(child.category_id, {
        ...payload,
        category_id: child.category_id,
        identifier: setShopIndentifier(selected.children[0].title),
      });

    request
      .then(() => {
        antdAsyncMessageSuccess({ text: "Successfully updated", key: "update_shop_category_modal" });
        refetch();
        onClose();
      })
      .catch((err) => {
        console.error("Error updating category:", err);
      });
  };

  return (
    <ModalSimple text={`Edit category: ${child.title}`} onClose={onClose} hide={true}>
      <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off">
        <Form.Item label="Group" name="group">
          <Input placeholder="Group" />
        </Form.Item>

        <Form.Item label="Brand" name="brand">
          <Select allowClear options={brands} placeholder="Select brand" />
        </Form.Item>

        <Form.List name="brandCodes">
          {(fields, { add, remove }) => (
            <div>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className="d-flex flex-row align-items-center mb-2">
                  <Form.Item {...restField} name={[name, "brand_id"]} className="mr-1 mb-0" style={{ width: "35%" }}>
                    <Select allowClear options={brands} placeholder="Brand" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "code"]} className="mr-1 mb-0" style={{ width: "30%" }}>
                    <Input placeholder="Brand Code" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "profit"]} className="mb-0" style={{ width: "25%" }}>
                    <Input placeholder="Profit" />
                  </Form.Item>
                  <div className="d-flex align-items-center ml-2" style={{ height: 32 }}>
                    {fields.length > 1 && (
                      <div onClick={() => remove(name)} className="cursor-pointer">
                        <IconTrash color="red" />
                      </div>
                    )}
                    {name === fields.length - 1 && (
                      <div onClick={() => add()} className="cursor-pointer ml-2">
                        <IconPlus color="blue" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {fields.length === 0 && (
                <Button type="dashed" onClick={() => add()}>
                  Add brand code
                </Button>
              )}
            </div>
          )}
        </Form.List>

        <div className="row">
          <div className="col-4">
            <Form.Item name="warranty" valuePropName="checked" label="Warranty">
              <Checkbox />
            </Form.Item>
          </div>
          <div className="col-4">
            <Form.Item name="withoutServices" valuePropName="checked" label="Without services">
              <Checkbox />
            </Form.Item>
          </div>
          <div className="col-4">
            <Form.Item label="Customs code" name="customsCode">
              <Input placeholder="Customs code" />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-4">
            <Form.Item name="reverseCharging" valuePropName="checked" label="Reverse charging">
              <Checkbox />
            </Form.Item>
          </div>
          <div className="col-4">
            <Form.Item label="Manager Code" name="managerCode">
              <Input placeholder="Manager Code" />
            </Form.Item>
          </div>
          <div className="col-4">
            <Form.Item label="Agata Code" name="agataCode">
              <Input placeholder="Agata Code" />
            </Form.Item>
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <Form.Item label="Agata Price" name="agataPrice">
              <Input placeholder="Agata Price" />
            </Form.Item>
          </div>
          <div className="col-6">
            <Form.Item label="Profit" name="profit">
              <Input placeholder="Profit" />
            </Form.Item>
          </div>
        </div>

        <Form.Item>
          <Button className="w-100" type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>
    </ModalSimple>
  );
};